import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../services/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ArrowBack,
  Save,
  Description,
  Work,
  AttachMoney,
  Add,
  Delete,
} from "@mui/icons-material";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import PageSkeleton from "../components/ui/PageSkeleton";

const validationSchema = Yup.object({
  title: Yup.string().required("Job title is required"),
  department_id: Yup.string().required("Department is required"),
  location: Yup.string().required("Location is required"),
  experience: Yup.string().required("Experience is required"),
  employment_type: Yup.string()
    .oneOf(["full-time", "part-time", "contract", "internship"])
    .required("Employment type is required"),
  description: Yup.string().required("Description is required"),
  responsibilities: Yup.array().of(Yup.string()),
  required_skills: Yup.array().of(Yup.string()),
  nice_to_have: Yup.array().of(Yup.string()),
  what_we_offer: Yup.array().of(Yup.string()),
  salary_range: Yup.object().shape({
    min: Yup.number().min(0),
    max: Yup.number().min(Yup.ref("min")),
  }),
  status: Yup.string().oneOf(["draft", "published", "closed"]).default("draft"),
});

export default function JobPostingCreate() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get("/departments");
      const deptData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setDepartments(deptData);
    } catch (err) {
      console.error("Failed to fetch departments", err);
      toast.error("Failed to load departments");
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      department_id: "",
      location: "",
      experience: "",
      employment_type: "full-time",
      description: "",
      responsibilities: [""],
      required_skills: [""],
      nice_to_have: [""],
      what_we_offer: [""],
      salary_range: { min: "", max: "" },
      status: "draft",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Filter out empty arrays and map fields
        const payload = {
          ...values,
          responsibilities: values.responsibilities.filter(
            (r) => r.trim() !== ""
          ),
          required_skills: values.required_skills.filter(
            (r) => r.trim() !== ""
          ),
          nice_to_have: values.nice_to_have.filter((r) => r.trim() !== ""),
          what_we_offer: values.what_we_offer.filter((r) => r.trim() !== ""),
          // Map required_skills to skills_required for backward compatibility
          skills_required: values.required_skills.filter(
            (r) => r.trim() !== ""
          ),
          salary_range:
            values.salary_range.min || values.salary_range.max
              ? {
                  min: values.salary_range.min
                    ? Number(values.salary_range.min)
                    : undefined,
                  max: values.salary_range.max
                    ? Number(values.salary_range.max)
                    : undefined,
                }
              : null,
        };

        await api.post("/recruitment/jobs", payload);
        toast.success("Job posting created successfully!");
        navigate("/recruitment");
      } catch (err) {
        console.error("Failed to create job posting", err);
        toast.error(
          err.response?.data?.error || "Failed to create job posting"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate("/recruitment")}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition-colors"
        >
          <ArrowBack className="w-5 h-5" />
          <span className="font-medium">Back to Recruitment</span>
        </button>
        <h1 className="text-4xl font-bold text-primary mb-2">
          Create Job Posting
        </h1>
        <p className="text-gray-600 text-lg">Post a new job opening</p>
      </motion.div>

      <form onSubmit={formik.handleSubmit}>
        <div className="card space-y-8">
          {/* Basic Information */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <Work className="w-6 h-6 text-primary" />
              </div>
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <Input
                  name="title"
                  placeholder="e.g., Senior Software Engineer"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && formik.errors.title}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  name="department_id"
                  value={formik.values.department_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="input-field"
                >
                  <option value="">Select Department</option>
                  {Array.isArray(departments) &&
                    departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
                {formik.touched.department_id &&
                  formik.errors.department_id && (
                    <p className="text-red-500 text-sm mt-1">
                      {formik.errors.department_id}
                    </p>
                  )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <Input
                  name="location"
                  placeholder="e.g., Mumbai, India / Remote / Hybrid"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.location && formik.errors.location}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Experience <span className="text-red-500">*</span>
                </label>
                <Input
                  name="experience"
                  placeholder="e.g., 5-8+ years"
                  value={formik.values.experience}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.experience && formik.errors.experience}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Employment Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="employment_type"
                  value={formik.values.employment_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="input-field"
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <Description className="w-6 h-6 text-primary" />
              </div>
              Job Description
            </h2>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                rows={6}
                placeholder="Describe the role, responsibilities, and what you're looking for..."
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="input-field resize-none"
              />
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {formik.errors.description}
                </p>
              )}
            </div>
          </div>

          {/* Key Responsibilities */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6">
              Key Responsibilities
            </h2>
            <div className="space-y-3">
              {formik.values.responsibilities.map((resp, index) => (
                <div key={index} className="flex gap-3">
                  <Input
                    placeholder={`Responsibility ${index + 1}`}
                    value={resp}
                    onChange={(e) => {
                      const newResponsibilities = [
                        ...formik.values.responsibilities,
                      ];
                      newResponsibilities[index] = e.target.value;
                      formik.setFieldValue(
                        "responsibilities",
                        newResponsibilities
                      );
                    }}
                    className="flex-1"
                  />
                  {formik.values.responsibilities.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newResponsibilities =
                          formik.values.responsibilities.filter(
                            (_, i) => i !== index
                          );
                        formik.setFieldValue(
                          "responsibilities",
                          newResponsibilities
                        );
                      }}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Delete className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  formik.setFieldValue("responsibilities", [
                    ...formik.values.responsibilities,
                    "",
                  ]);
                }}
                className="btn-secondary flex items-center gap-2 w-full md:w-auto"
              >
                <Add className="w-5 h-5" />
                Add Responsibility
              </button>
            </div>
          </div>

          {/* Required Skills & Qualifications */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6">
              Required Skills & Qualifications
            </h2>
            <div className="space-y-3">
              {formik.values.required_skills.map((skill, index) => (
                <div key={index} className="flex gap-3">
                  <Input
                    placeholder={`Required skill/qualification ${index + 1}`}
                    value={skill}
                    onChange={(e) => {
                      const newSkills = [...formik.values.required_skills];
                      newSkills[index] = e.target.value;
                      formik.setFieldValue("required_skills", newSkills);
                    }}
                    className="flex-1"
                  />
                  {formik.values.required_skills.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newSkills = formik.values.required_skills.filter(
                          (_, i) => i !== index
                        );
                        formik.setFieldValue("required_skills", newSkills);
                      }}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Delete className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  formik.setFieldValue("required_skills", [
                    ...formik.values.required_skills,
                    "",
                  ]);
                }}
                className="btn-secondary flex items-center gap-2 w-full md:w-auto"
              >
                <Add className="w-5 h-5" />
                Add Required Skill
              </button>
            </div>
          </div>

          {/* Nice to Have */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6">
              Nice to Have
            </h2>
            <div className="space-y-3">
              {formik.values.nice_to_have.map((item, index) => (
                <div key={index} className="flex gap-3">
                  <Input
                    placeholder={`Nice to have ${index + 1}`}
                    value={item}
                    onChange={(e) => {
                      const newItems = [...formik.values.nice_to_have];
                      newItems[index] = e.target.value;
                      formik.setFieldValue("nice_to_have", newItems);
                    }}
                    className="flex-1"
                  />
                  {formik.values.nice_to_have.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = formik.values.nice_to_have.filter(
                          (_, i) => i !== index
                        );
                        formik.setFieldValue("nice_to_have", newItems);
                      }}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Delete className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  formik.setFieldValue("nice_to_have", [
                    ...formik.values.nice_to_have,
                    "",
                  ]);
                }}
                className="btn-secondary flex items-center gap-2 w-full md:w-auto"
              >
                <Add className="w-5 h-5" />
                Add Nice to Have
              </button>
            </div>
          </div>

          {/* What We Offer */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6">
              What We Offer
            </h2>
            <div className="space-y-3">
              {formik.values.what_we_offer.map((offer, index) => (
                <div key={index} className="flex gap-3">
                  <Input
                    placeholder={`Benefit/offer ${index + 1}`}
                    value={offer}
                    onChange={(e) => {
                      const newOffers = [...formik.values.what_we_offer];
                      newOffers[index] = e.target.value;
                      formik.setFieldValue("what_we_offer", newOffers);
                    }}
                    className="flex-1"
                  />
                  {formik.values.what_we_offer.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newOffers = formik.values.what_we_offer.filter(
                          (_, i) => i !== index
                        );
                        formik.setFieldValue("what_we_offer", newOffers);
                      }}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Delete className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  formik.setFieldValue("what_we_offer", [
                    ...formik.values.what_we_offer,
                    "",
                  ]);
                }}
                className="btn-secondary flex items-center gap-2 w-full md:w-auto"
              >
                <Add className="w-5 h-5" />
                Add Benefit
              </button>
            </div>
          </div>

          {/* Salary Range */}
          <div>
            <h2 className="text-2xl font-bold text-primary mb-6 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                <AttachMoney className="w-6 h-6 text-primary" />
              </div>
              Salary Range (Optional)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Minimum Salary
                </label>
                <Input
                  name="salary_range.min"
                  type="number"
                  placeholder="e.g., 500000"
                  value={formik.values.salary_range.min}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Maximum Salary
                </label>
                <Input
                  name="salary_range.max"
                  type="number"
                  placeholder="e.g., 1000000"
                  value={formik.values.salary_range.max}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="input-field"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/recruitment")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={formik.isSubmitting}
            >
              <Save className="w-5 h-5" />
              {formik.isSubmitting ? "Creating..." : "Create Job Posting"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
